import { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import { WooRequestFieldsFragment } from 'api/graphql-types';
import { instanceOfWooZip } from 'utils/type-guard/woo-zip';
import { formatBytes } from 'utils/format-bytes';
import { BaseModule } from 'modules/base-module/base-module';
import { Button } from 'components/button';

import tableStyles from 'modules/table-module/table-module.module.scss';
import styles from './open-documents.module.scss';
import MobileTable from 'modules/table-module/mobile-table';

export const OPEN_DOCUMENTS_LIMIT = 3;

const OpenDocuments = ({ zip, files }: WooRequestFieldsFragment): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const filesArray = files?.filter((file) => !!file.data.attributes?.url) || [];
	const filesCount = filesArray?.length || 0;
	const hasFiles = filesCount > 0;
	const hasZip = zip;
	const hasContent = hasFiles || hasZip;

	const [showRows, setShowRows] = useState<boolean>(false);

	if (!hasContent) return <></>;

	const fileTableHeaders = [
		formatMessage({ id: 'open.woo.detail.documents.files.table.name' }),
		formatMessage({ id: 'open.woo.detail.documents.files.table.type' }),
		formatMessage({ id: 'open.woo.detail.documents.files.table.download' })
	];

	return (
		<BaseModule
			defaultModuleOptions={{
				__typename: 'ComponentInternalDefaultModuleOptions',
				backgroundColor: 'grey',
				padding: 'small'
			}}
		>
			<div className={styles.header}>
				<h2>
					{formatMessage({
						id: filesCount > 1 ? 'open.woo.detail.documents.title.plural' : 'open.woo.detail.documents.title.single'
					})}
				</h2>

				{zip && instanceOfWooZip(zip) && filesCount > 0 && (
					<div>
						{formatMessage(
							{ id: 'open.woo.detail.documents.fileCountWithSize' },
							{ count: filesCount, size: formatBytes(zip.size || 0) }
						)}
					</div>
				)}

				{zip && instanceOfWooZip(zip) && (
					<Button title={formatMessage({ id: 'open.woo.detail.documents.zip.button' })} href={zip.url} />
				)}
			</div>

			{filesCount > 0 && (
				<div className={clsx(styles.table, tableStyles.schedulingTable)}>
					<table>
						<thead>
							<tr>
								{fileTableHeaders.map((header, index) => (
									<th key={`file-table-header-${index}-${header}`}>{header}</th>
								))}
							</tr>
						</thead>

						<tbody>
							{filesArray.map(({ data }, index) => (
								<tr
									key={`file-table-body-${index}-${data?.attributes?.name}`}
									style={{ display: !showRows && index + 1 > OPEN_DOCUMENTS_LIMIT ? 'none' : 'table-row' }}
								>
									<td>{data?.attributes?.name}</td>
									<td width={180}>
										{data?.attributes?.size && formatBytes(data?.attributes?.size)}
										{data?.attributes?.ext && ` (${data?.attributes?.ext})`}
									</td>
									<td width={180}>
										<Button
											appearance="ghost"
											variant="tertiary-dark"
											href={data?.attributes?.url}
											title={formatMessage({ id: 'open.woo.detail.documents.files.button' })}
										></Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<MobileTable
						body={[
							(showRows ? filesArray : filesArray.slice(0, OPEN_DOCUMENTS_LIMIT)).map(({ data }, index) => (
								<div className={styles.cell} key={`file-table-body-${index}-${data?.attributes?.name}`}>
									<div>{data?.attributes?.name}</div>
									<div className={styles.cellMetadata}>
										<div>
											{data?.attributes?.size && formatBytes(data?.attributes?.size)}
											{data?.attributes?.ext && ` (${data?.attributes?.ext})`}
										</div>
										<div>
											<Button
												appearance="ghost"
												variant="tertiary-dark"
												href={data?.attributes?.url}
												title={formatMessage({ id: 'open.woo.detail.documents.files.button' })}
											></Button>
										</div>
									</div>
								</div>
							))
						]}
					/>

					{filesCount > OPEN_DOCUMENTS_LIMIT && (
						<Button
							appearance="ghost"
							variant="tertiary-dark"
							onClick={() => setShowRows((previous) => !previous)}
							title={formatMessage(
								{
									id: showRows
										? 'open.woo.detail.documents.files.toggle.close'
										: 'open.woo.detail.documents.files.toggle.open'
								},
								{ count: filesCount }
							)}
						/>
					)}
				</div>
			)}
		</BaseModule>
	);
};

export default OpenDocuments;
